<template>
  <div class="vhelp">
    <div class="vhelp__backdrop" @click="close('dismissed')" />

    <div
      ref="dialogRef"
      class="vhelp__dialog"
      role="dialog"
      aria-modal="true"
      tabindex="-1"
      :aria-label="t('help.aria.dialog')"
      @keydown="trapTab"
    >
      <div class="vhelp__bar">
        <v-icon size="18" color="primary" class="mr-2">{{ tour?.icon }}</v-icon>
        <!-- Where the step is, in the product's own words: the chapter, the view, and what the frame
             says — the record's name, the tab that is open on it. The last crumb is the one that
             places the step, so it is the one that keeps its width when the row runs out. -->
        <span class="vhelp__eyebrow">
          <template v-for="(crumb, index) in crumbs" :key="`${index}-${crumb}`">
            <span v-if="index" class="vhelp__crumb-sep" aria-hidden="true">›</span>
            <span class="vhelp__crumb text-truncate" :class="{ 'is-last': index === crumbs.length - 1 }">
              {{ crumb }}
            </span>
          </template>
        </span>
        <!-- The frame shows an invented week, and it says so. One chip is enough: the reader's own
             page is visibly still behind this dialog, so nothing of theirs has been painted over. -->
        <v-chip size="x-small" variant="tonal" color="warning" class="ml-3">
          <v-icon start size="12">mdi-flask-outline</v-icon>
          {{ t("help.demo.chip") }}
        </v-chip>
        <v-spacer />
        <v-btn
          icon="mdi-close"
          variant="text"
          size="small"
          density="comfortable"
          :aria-label="t('help.action.close')"
          aria-keyshortcuts="Escape"
          @click="close('dismissed')"
        />
      </div>

      <lew-view-help-stage @frame-keydown="onFrameKeydown">
        <!-- What a key just did, where the eye already is: pausing only swaps a small icon in the
             player bar, and a speed picked by key changes nothing else on screen at all. -->
        <transition name="vhelp-flash">
          <div v-if="flash" :key="flash.id" class="vhelp__flash" aria-hidden="true">
            <v-icon v-if="flash.icon" size="30">{{ flash.icon }}</v-icon>
            <span v-else>{{ flash.label }}</span>
          </div>
        </transition>
        <p class="d-sr-only" role="status">{{ announcement }}</p>
        <lew-view-help-end />
      </lew-view-help-stage>

      <lew-view-help-player :shortcuts-open="shortcutsOpen" @toggle-shortcuts="toggleShortcuts" />

      <lew-view-help-shortcuts :open="shortcutsOpen" @close="closeShortcuts" />
    </div>
  </div>
</template>

<script setup lang="ts">
/**
 * The walkthrough's dialog: the frame with its camera (`LewViewHelpStage`), the player bar
 * (`LewViewHelpPlayer`) and the shortcut list, with the keyboard and focus handling around them.
 *
 * Mounted by `LewViewHelp` only while a walkthrough is open, so every piece of state in here and in
 * its parts — a menu left open, a peek, a half-played flash — starts fresh with each one.
 */
import { computed, onMounted, ref } from "vue"
import { useViewHelp } from "@/composables/viewHelp/useViewHelp"
import { useViewHelpCopy } from "@/composables/viewHelp/useViewHelpCopy"
import { useViewHelpKeyboard } from "@/composables/viewHelp/useViewHelpKeyboard"

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

const { tour, close } = useViewHelp()
const { t, tourTitle, trail } = useViewHelpCopy()

const dialogRef = ref<HTMLElement | null>(null)

/** The header's trail, or the tour's own name while there is none. Crumb texts can repeat, so keyed by place. */
const crumbs = computed(() => (trail.value.length ? trail.value : [tourTitle.value]))
const { shortcutsOpen, flash, announcement, toggleShortcuts, closeShortcuts, onFrameKeydown } =
  useViewHelpKeyboard(dialogRef)

/**
 * Keeps Tab inside the dialog. The frame carries `tabindex="-1"`, so tabbing never walks into the app
 * inside it; the shortcut list keeps Tab to itself while it is open.
 */
function trapTab(event: KeyboardEvent) {
  if (event.key !== "Tab") return
  const focusable = Array.from(dialogRef.value?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? [])
  if (!focusable.length) return

  const first = focusable[0]
  const last = focusable[focusable.length - 1]

  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault()
    last.focus()
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault()
    first.focus()
  }
}

// Focus goes to the dialog once and stays put. The player moves on by itself every few seconds, and
// focus chasing it would drag a screen-reader user along — the text is announced through `aria-live`
// instead. Where it goes back to on close is `LewViewHelp`'s.
onMounted(() => dialogRef.value?.focus())
</script>

<style scoped>
/*
 * Scoped styles survive the teleport: the data-v attribute is stamped on these elements themselves.
 * (Precedent: LewOnboardingTour, GeneralContextMenu.)
 *
 * z-index: the app bar and navigation drawer sit at 1000-1004 (Vuetify ROOT_ZINDEX plus layout
 * items) and Vuetify's own overlays start at 2000, so 2510+ paints above an open menu. It stays above
 * LewOnboardingTour's 2500/2502 and below the personnel-planning context menus at 9998 and the
 * construction-report date picker at 10000. Vuetify tooltips and menus would open at 2000-something,
 * i.e. *behind* this dialog, which is why the timeline's peek and the speed menu are built by hand.
 * NB: the comment in PersonnelPlanningDetail.vue claiming Vuetify overlays are at 2400 is wrong.
 */

/*
 * How long a step's dot and its text take to go. Shared, because they must go together: when the dot
 * used its own timing, it outlived the text and hung on at the old region through the next camera
 * move. Inherited by the stage, which uses it.
 */
.vhelp {
  --vhelp-leave: 0.12s;
}

.vhelp__backdrop {
  position: fixed;
  inset: 0;
  z-index: 2510;
  background: rgba(0, 0, 0, 0.62);
  backdrop-filter: blur(2px);
}

.vhelp__dialog {
  position: fixed;
  z-index: 2511;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  width: min(1180px, calc(100vw - 32px));
  max-height: calc(100dvh - 32px);
  overflow-y: auto;
  border-radius: 16px;
  background: rgb(var(--v-theme-surface));
  color: rgb(var(--v-theme-on-surface));
  box-shadow: 0 18px 60px rgba(0, 0, 0, 0.4);
  outline: none;
}

.vhelp__bar {
  position: sticky;
  top: 0;
  z-index: 5;
  display: flex;
  align-items: center;
  flex: none;
  padding: 10px 12px 10px 18px;
  background: rgb(var(--v-theme-surface));
  border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  border-radius: 16px 16px 0 0;
}

/*
 * The trail. One line, and it gives way from the left: the last crumb — the tab, or the record —
 * is what places the step, so it keeps its width while the ones before it truncate.
 */
.vhelp__eyebrow {
  display: flex;
  align-items: baseline;
  gap: 5px;
  min-width: 0;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  opacity: 0.65;
}

.vhelp__crumb {
  min-width: 0;
  flex: 0 1 auto;
}

.vhelp__crumb.is-last {
  flex: none;
  opacity: 1;
}

.vhelp__crumb-sep {
  flex: none;
  opacity: 0.5;
}

/* ── key feedback, over the stage ────────────────────────────────────────── */

/* Over everything on the stage, loading cover included, and never in the way of a click. */
.vhelp__flash {
  position: absolute;
  top: 50%;
  left: 50%;
  z-index: 7;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 64px;
  height: 64px;
  padding: 0 18px;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.62);
  color: #ffffff;
  font-size: 1.1rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  pointer-events: none;
  transform: translate(-50%, -50%);
}

.vhelp-flash-enter-active {
  transition:
    opacity 0.12s ease,
    transform 0.12s ease;
}

.vhelp-flash-leave-active {
  transition:
    opacity 0.3s ease,
    transform 0.3s ease;
}

.vhelp-flash-enter-from {
  opacity: 0;
  transform: translate(-50%, -50%) scale(0.85);
}

.vhelp-flash-leave-to {
  opacity: 0;
  transform: translate(-50%, -50%) scale(1.15);
}

@media (max-width: 700px) {
  .vhelp__dialog {
    width: calc(100vw - 16px);
  }
}

@media (prefers-reduced-motion: reduce) {
  .vhelp-flash-enter-active,
  .vhelp-flash-leave-active {
    transition: none;
  }
}
</style>
